import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const FAQSection = () => {
  const reduceMotion = useReducedMotion();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [openItems, setOpenItems] = useState([]);

  const faqs = [
    {
      question: 'Was ist der Unterschied zwischen systemischer Beratung und Psychotherapie?',
      answer:
        'Systemische Beratung fokussiert sich auf konkrete Anliegen und Lösungen im Hier und Jetzt. Sie ist zeitlich begrenzt und zielorientiert. Psychotherapie hingegen behandelt psychische Erkrankungen und kann länger dauern. Als Beraterin arbeite ich präventiv und unterstützend, nicht heilend.',
      category: 'Grundlagen',
    },
    {
      question: 'Wie lange dauert eine Beratung insgesamt?',
      answer:
        'Die Dauer ist sehr individuell und hängt von Ihrem Anliegen ab. Manche Themen lassen sich in 3-5 Sitzungen bearbeiten, andere benötigen 10-15 Termine. Wir besprechen gemeinsam Ihre Ziele und passen den Prozess entsprechend an.',
      category: 'Ablauf',
    },
    {
      question: 'Werden die Kosten von der Krankenkasse übernommen?',
      answer:
        'Systemische Beratung wird in der Regel nicht von der gesetzlichen Krankenkasse übernommen.',
      category: 'Kosten',
    },
    {
      question: 'Kann ich auch online beraten werden?',
      answer:
        'Ja, ich biete auch Videoberatungen an. Diese finden über eine sichere, datenschutzkonforme Plattform statt. Online-Beratung eignet sich besonders bei weiten Anfahrtswegen oder eingeschränkter Mobilität.',
      category: 'Ablauf',
    },
    {
      question: 'Was passiert beim Erstgespräch?',
      answer:
        'Im Erstgespräch lernen wir uns kennen, besprechen Ihr Anliegen und klären organisatorische Fragen. Das Gespräch dauert in der Regel 60-90 Minuten. Danach entscheiden Sie, ob Sie die Beratung fortsetzen möchten.',
      category: 'Ablauf',
    },
    {
      question: 'Unterliegen Sie der Schweigepflicht?',
      answer:
        'Ja, alle Inhalte der Beratung unterliegen der gesetzlichen Schweigepflicht. Ihre persönlichen Informationen werden vertraulich behandelt. Ausnahmen bestehen nur bei akuter Selbst- oder Fremdgefährdung und auch dann nur nach Rücksprache mit Ihnen.',
      category: 'Rechtliches',
    },
    {
      question: 'Kann ich Termine kurzfristig absagen?',
      answer:
        'Sie können Termine bis zu 24 Stunden vorher kostenfrei absagen oder verschieben. Bei kurzfristigeren Absagen oder Nicht-Erscheinen berechne ich das vereinbarte Honorar, ausgenommen bei Krankheit mit ärztlichem Attest.',
      category: 'Organisatorisches',
    },
    {
      question: 'Für wen ist systemische Beratung geeignet?',
      answer:
        'Systemische Beratung eignet sich für Einzelpersonen, Paare und Familien, die ihre Themen, Beziehungen oder Lebenssituation klären und neue Perspektiven entwickeln möchten. Ob Beziehungsfragen, berufliche Herausforderungen, Erziehungsfragen oder persönliche Krisen – der systemische Ansatz bietet vielfältige Unterstützung und Orientierung.',
      category: 'Grundlagen',
    },
    {
      question: 'Was bedeutet „systemisch“ konkret?',
      answer:
        'Systemisch bedeutet, dass Sie nicht isoliert betrachtet werden, sondern im Kontext Ihrer Beziehungen und Lebensumstände. Probleme und Herausforderungen entstehen oft in Wechselwirkungen, daher berücksichtigen wir Muster, Ressourcen und mögliche Lösungen in Ihrem gesamten Umfeld.',
      category: 'Grundlagen',
    },
    {
      question: 'Muss mein Partner/meine Familie mit zur Beratung kommen?',
      answer:
        'Nein, das ist nicht zwingend nötig. Auch in der Einzelberatung kann systemisch gearbeitet werden. Manchmal ist es jedoch hilfreich, wichtige Bezugspersonen einzubeziehen. Dies besprechen wir individuell.',
      category: 'Ablauf',
    },
  ];

  const categories = useMemo(() => [...new Set(faqs.map((f) => f.category))], [faqs]);

const filteredFAQs = useMemo(() => {
  let results = faqs;

  const q = searchTerm.trim().toLowerCase();
  if (q) {
    results = results.filter(
      (faq) =>
        faq.question.toLowerCase().includes(q) ||
        faq.answer.toLowerCase().includes(q) ||
        faq.category.toLowerCase().includes(q)
    );
  }

  if (activeCategory !== 'Alle') {
    results = results.filter((faq) => faq.category === activeCategory);
  }

  return results;
}, [faqs, searchTerm, activeCategory]);

  const toggleItem = (index) => {
    setOpenItems((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const openByCategory = (category) => {
    // indexes in "filteredFAQs" are not stable vs "faqs", so we operate on original faqs
    const categoryIndexes = faqs
      .map((faq, idx) => (faq.category === category ? idx : null))
      .filter((v) => v !== null);

    setOpenItems((prev) => {
      const allOpen = categoryIndexes.every((i) => prev.includes(i));
      return allOpen ? prev.filter((i) => !categoryIndexes.includes(i)) : [...new Set([...prev, ...categoryIndexes])];
    });
  };

  const sectionAnim = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: reduceMotion ? 0 : 0.55, ease: 'easeOut' } },
  };

  return (
    <section id="faq" className="py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div ref={ref} initial="hidden" animate={inView ? 'show' : 'hidden'} variants={sectionAnim}>
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Häufig gestellte Fragen
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Hier finden Sie Antworten auf die wichtigsten Fragen. Nutzen Sie gerne die Suche oder kontaktieren Sie mich
              direkt.
            </p>
            <div className="section-divider" />
          </div>

          {/* Suche */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <MagnifyingGlassIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                style={{ color: 'var(--color-text-muted)', opacity: 0.9 }}
              />
              <input
                type="text"
                placeholder="Suchen Sie nach Stichworten…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3"
                style={{
                  paddingLeft: '2.5rem',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--color-border-subtle)',
                  backgroundColor: 'var(--color-surface-1)',
                  color: 'var(--color-text-primary)',
                  boxShadow: 'var(--shadow-sm)',
                }}
              />
            </div>

            {searchTerm && (
              <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>
                {filteredFAQs.length} {filteredFAQs.length === 1 ? 'Ergebnis' : 'Ergebnisse'} gefunden
              </p>
            )}
          </div>

          {/* Kategorien */}
          {!searchTerm && (
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              {['Alle', ...categories].map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)} // Setzt jetzt den Filter
                  className="px-4 py-2 text-sm"
                  style={{
                    borderRadius: 'var(--radius-pill)',
                    // Optisches Feedback, welcher Button aktiv ist:
                    backgroundColor: activeCategory === category 
                      ? 'var(--color-text-primary)' 
                      : 'var(--color-bg-tertiary)',
                    color: activeCategory === category 
                      ? 'var(--color-surface-1)' 
                      : 'var(--color-text-secondary)',
                    border: '1px solid var(--color-border-subtle)',
                    transition: 'all 120ms ease',
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {/* Accordion */}
          <div className="max-w-3xl mx-auto space-y-3">
            <AnimatePresence initial={false}>
              {filteredFAQs.map((faq, index) => {
                const key = faq.question;
                const isOpen = openItems.includes(faqs.findIndex((f) => f.question === faq.question));

                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: reduceMotion ? 0 : 0.22, ease: 'easeOut' }}
                    className="surface p-5"
                    style={{ borderRadius: 'var(--radius-xl)' }}
                  >
                    <button
                      onClick={() => toggleItem(faqs.findIndex((f) => f.question === faq.question))}
                      className="w-full flex items-start justify-between text-left gap-4"
                      aria-expanded={isOpen}
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          {faq.question}
                        </h3>

                        <span
                          className="mt-1 inline-flex items-center"
                          style={{ color: 'var(--color-text-muted)', fontSize: 12 }}
                        >
                          {faq.category}
                        </span>
                      </div>

                      <ChevronDownIcon
                        className="w-5 h-5 flex-shrink-0 mt-0.5"
                        style={{
                          color: 'var(--color-text-muted)',
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 160ms ease',
                        }}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: reduceMotion ? 0 : 0.22, ease: 'easeOut' }}
                          className="overflow-hidden"
                        >
                          <div
                            className="pt-4 leading-relaxed"
                            style={{ color: 'var(--color-text-secondary)' }}
                          >
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {filteredFAQs.length === 0 && (
            <div className="text-center py-12">
              <p style={{ color: 'var(--color-text-muted)' }}>
                Keine Ergebnisse gefunden. Bitte versuchen Sie es mit anderen Suchbegriffen.
              </p>
            </div>
          )}

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: reduceMotion ? 0 : 0.35 }}
            className="text-center mt-12 p-8"
            style={{
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--color-border-subtle)',
              background: 'var(--color-bg-secondary)',
              boxShadow: 'var(--shadow-sm)',
            }}
          >
            <h3 className="font-serif text-xl mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Ihre Frage war nicht dabei?
            </h3>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              Kein Problem! Kontaktieren Sie mich gerne direkt für ein persönliches Gespräch.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn btn-primary"
                style={{ borderRadius: 'var(--radius-pill)' }}
              >
                Frage stellen
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;